import type { CSSProperties } from 'react';
import type { HeroBox, Outline } from './realImages';

// THE PICTURE, MOVED SO THE BOX FILLS ITS FRAME — a transform on the image, not a crop of
// it. What cuts a listing crop down to a tile: see CropStrip.
//
// The other way round would be to cut the rectangle out (a background blown up and offset,
// or a canvas). Cutting needs the crop's TRUE aspect ratio to avoid stretching it, and the
// four numbers here cannot give it: they are fractions of a picture whose own proportions
// are only known once it has loaded. Every version of that arithmetic was right on square
// photographs and quietly wrong on the rest.
//
// A transform needs none of it. The image keeps its own shape, layout still sees it at its
// untransformed size, and the parent's overflow does the cutting. Translate first
// (percentages of the image, moving the box's centre to the picture's centre), then scale
// about that centre — so the parent must CENTRE the image for the box to land in the
// middle of it.
// How much room is left around a box when it is shown as a tile. Exported because a caller
// that shapes its frame to the box has to shape it to the PADDED box, or the two arithmetics
// disagree by exactly this margin — see CropStrip.
export const BOX_MARGIN = 0.02;

export function boxZoomStyle(
  box: HeroBox, margin = BOX_MARGIN, most = 14, fit: 'contain' | 'width' | 'height' = 'contain',
  // For 'height' only: the frame's width over its height, over the picture's own width over
  // height. The image is laid out at the frame's WIDTH, so filling the frame's height with the
  // box needs both shapes.
  shapes = 1,
): CSSProperties {
  const [x0, y0, x1, y1] = box;
  // Room around it, because a rectangle filled edge to edge with its subject shows the
  // subject and none of what settles the question — a wood button is a wood button
  // because of the leg it is in.
  const w = Math.min(1, Math.max(1e-4, x1 - x0) + margin * 2);
  const h = Math.min(1, Math.max(1e-4, y1 - y0) + margin * 2);
  // WHICH SIDE HAS TO FIT.
  //
  // 'contain' fits the LARGER of the two, which is the only safe answer in a frame whose
  // shape is not the box's: the whole box lands inside it and the rest of the frame fills
  // with surroundings. It is what a square tile and a full-screen zoom both want.
  //
  // 'width' fits the box's width and lets the height fall where it falls. Correct only for a
  // caller that has already shaped its frame to the box (CropStrip does, from the picture's
  // measured aspect) — and there it is exact in BOTH axes, because that is what shaping the
  // frame bought. Under 'contain' the same tile would under-scale a tall box by h/w and show
  // the chair around it, which is the bug this option exists for.
  //
  // Past a point magnification is just bigger pixels either way: a part fourteen times up is
  // as much as the photograph actually knows.
  const scale = Math.min(most, 1 / (fit === 'width' ? w
    : fit === 'height' ? h * shapes : Math.max(w, h)));
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  return {
    transform: `scale(${scale}) translate(${(0.5 - cx) * 100}%, ${(0.5 - cy) * 100}%)`,
  };
}

// One drawn rectangle, and whether somebody has turned it off. Several of these on one
// picture is a part that is in it several times.
//
// `outline` is the part traced inside that rectangle, where the model sent one. It is drawn
// because it is READ: the colour of the part comes from the pixels this polygon encloses, so
// a wrong outline is a wrong colour, and until it was on screen there was no way to tell one
// from the other — a chair leg and the wall behind it are both a swatch.
// `label` names the part it is drawn around, and `tone` is the colour that part is drawn in
// everywhere — the two halves of reading a picture with several parts on it. Between them
// they are the difference between a picture of green shapes and a picture you can read.
export type DrawnBox = {
  box: HeroBox; off?: boolean; outlines?: Outline[]; label?: string; tone?: string;
  // The part's size on the two edges that show it — see BoxOverlay, which draws them.
  across?: string; up?: string;
  frame?: boolean;
  // NOT DRAWN AT ALL, and still in the list. The zoom hands back the POSITION of the box
  // that was clicked, so the caller reads its own crops by that index — filter the hidden
  // ones out and every strike-through after the first hidden part lands on the wrong crop.
  hidden?: boolean;
};

// TURNED OFF: hatched, and grey. Diagonal stripes because a dashed outline alone reads as a
// style of box rather than a state of one, and the fill is what makes a struck-through
// detection legible at thumbnail size, where the border is three pixels of anything.
const HATCH =
  'repeating-linear-gradient(45deg, rgba(148,163,184,0.55) 0 4px, transparent 4px 9px)';

// The crop, drawn on a picture: what will be kept, outlined, everything else dimmed.
//
// Read-only, and the whole of what the crop LOOKS like — CropEditor adds handles on
// top of this and nothing else. It exists because the same rectangle now has to appear
// on three sizes of the same photo (the editor, its thumbnail, the zoom), and three
// copies of a border colour and a dim opacity would drift apart the first time one of
// them was tuned.
//
// Positioned in percentages, which is why the box is normalized: the same four numbers
// land on the same pixels whatever size the picture renders at.
//
// It fills its parent, so THE PARENT MUST BE THE PICTURE — a `relative` element whose
// rect is the painted image, not a box the image sits inside. An <img> letterboxed by
// `object-contain` does not fill its element, and a box drawn on that element would sit
// off the photo by the size of the letterbox.
export function BoxOverlay({
  box, outlines, label, labelInside = false, tone, rounded = 'rounded-lg', off = false, veil = true,
  frame = true, across, up, onClick, title,
}: {
  box: HeroBox;
  // The part traced within the rectangle, in the SAME normalized space — so it needs no
  // alignment against the box and none against the picture's size. Drawn filled, because at
  // thumbnail size a stroke this thin is a smudge and the fill is what says "these pixels".
  //
  // ONE POLYGON PER PIECE, drawn as separate shapes and never joined: the gap between a
  // frame's two legs belongs to the room behind the chair, and a single ring around both of
  // them would claim it.
  outlines?: Outline[];
  // Which part this box is around, written on it. It rides the TOP EDGE of the rectangle,
  // dropping inside when the box starts too near the top of the picture to have room above
  // — a chip clipped by the frame is worse than none, since it still takes the pixels.
  label?: string;
  // ALWAYS INSIDE the box's top-left corner, never above it. The above-or-inside test is a
  // fraction of the picture, and on a thumbnail a chip is a far larger fraction than on a
  // zoom: a box a tenth of the way down a 120-pixel tile had its chip cut in half by the frame.
  labelInside?: boolean;
  // THE COLOUR THIS PART IS DRAWN IN, wherever it is drawn. A CSS colour, not a class: it is
  // picked per part at the call site and Tailwind can only emit classes it can see written
  // out, so a class name assembled from an index is a class that does not exist.
  //
  // Omitted, everything is the app's emerald — which is right for a picture with one claim on
  // it, and useless for one with nine. Struck through still wins over it: grey is what says
  // "somebody disagreed", and a box that keeps its part's colour after being dismissed is
  // still asserting something.
  tone?: string;
  // THE PART'S SIZE, WRITTEN ON THE EDGES THAT SHOW IT — already formatted, already chosen.
  // `across` rides the bottom edge and `up` the right one, and which of x/y/z each is comes
  // from the photograph's own direction: on a front view the horizontal edge is the part's
  // width and on a side view it is its depth. Both optional, independently: half a size is
  // half an answer and worth drawing.
  across?: string;
  up?: string;
  rounded?: string;
  // THE RECTANGLE ITSELF, AND ITS NAME. Off, only the traced shape is drawn — the picture
  // keeps the answer and loses the frame around it, which is what the zoom's BOXES switch
  // turns off. The span stays where it was, invisible and still clickable: striking a
  // detection through is aimed at the box, and taking the border away is not a reason to
  // take the target away with it.
  frame?: boolean;
  // Struck through by hand: hatched and grey instead of drawn in the app's own colour, and
  // never veiled — a box nobody believes should not be dimming the picture around it in
  // order to be looked at.
  off?: boolean;
  // The dim over everything outside the rectangle. It is the rectangle's OWN shadow, so it
  // does not compose: two of them on one picture veil each other's box, and what should
  // read as "the part is in these four places" reads as three mistakes and one answer. So
  // a lone box keeps the veil and a set of them is drawn as outlines.
  veil?: boolean;
  // Given, the rectangle itself is clickable — the rest of the picture is not, and goes on
  // behaving as whatever it was (a backdrop that closes, usually). The box is the one thing
  // on a photograph worth aiming at, so it is the one thing that takes a click.
  onClick?: () => void;
  title?: string;
}) {
  const [x0, y0, x1, y1] = box;
  // The part's own colour, or none — struck through is grey whatever part it belonged to.
  const paint = off ? null : tone;
  return (
    // The dim is the rectangle's own enormous spread shadow, so it has to be clipped
    // to the picture: unclipped, a shadow that size veils the whole page and paints
    // over everything that is not a later positioned sibling. The clip lives here
    // rather than at each call site — a caller who forgets it cannot be seen to have
    // forgotten it locally; the damage shows up somewhere else entirely.
    <span aria-hidden className={`pointer-events-none absolute inset-0 overflow-hidden ${rounded}`}>
      <span
        onClick={onClick && ((e) => { e.stopPropagation(); onClick(); })}
        title={title}
        style={{
          left: `${x0 * 100}%`,
          top: `${y0 * 100}%`,
          width: `${(x1 - x0) * 100}%`,
          height: `${(y1 - y0) * 100}%`,
          ...(off && frame ? { backgroundImage: HATCH } : {}),
          ...(paint && frame ? { borderColor: paint } : {}),
        }}
        className={`absolute rounded-sm ${frame ? 'border-2' : ''} ${
          !frame ? '' : off ? 'border-dashed border-slate-300' : paint ? '' : 'border-blue-500'
        } ${veil && !off && frame ? 'shadow-[0_0_0_9999px_rgba(15,23,42,0.55)]' : ''} ${
          onClick
            ? `pointer-events-auto cursor-pointer ${
              off ? 'hover:border-slate-100' : 'hover:border-blue-300'}`
            : ''
        }`}
      />
      {/* AFTER THE BOX, so the veil — which is the box's own outward shadow — cannot dim the
          one mark on the picture that is worth looking closely at.

          `preserveAspectRatio="none"` maps the 0..1 space straight onto the picture's own
          rect however that rect is shaped, which is what makes these coordinates need no
          arithmetic here. It also squashes the stroke, so the stroke is taken out of the
          transform: non-scaling-stroke is a constant width in device pixels, the same hairline
          on a 90-pixel tile and on a full-screen zoom. */}
      {!!outlines?.length && (
        <svg
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
          className="pointer-events-none absolute inset-0 h-full w-full"
        >
          {outlines.filter((ring) => ring.length > 2).map((ring, i) => (
            <polygon
              key={i}
              points={ring.map(([x, y]) => `${x},${y}`).join(' ')}
              vectorEffect="non-scaling-stroke"
              strokeWidth={1.5}
              strokeLinejoin="round"
              // The same colour twice, at two strengths: a wash to say which pixels, an opaque
              // edge to say exactly where they stop. One flat fill does neither well.
              {...(paint ? { fill: paint, fillOpacity: 0.3, stroke: paint } : {})}
              className={paint ? undefined : off
                ? 'fill-slate-400/25 stroke-slate-200'
                : 'fill-blue-400/30 stroke-blue-300'}
            />
          ))}
        </svg>
      )}
      {/* THE NAME, LAST — over the box, over the fill, over the veil. Anything drawn after it
          tints it, and a label read through 30% emerald is a label nobody trusts.

          On the box's top edge: above the line where there is room, just inside it where there
          is not. A box at the very top of a photograph has nothing above it but the frame, and
          a chip put there is cut in half by the clip this span exists to apply. */}
      {/* THE WRITING IS IN FRONT OF EVERY BOX, not just its own. Each rectangle is drawn by
          its own BoxOverlay, so on a picture with six parts the sixth one's border painted
          over the first one's name — and a label crossed out by a line that belongs to
          another part is the one mark here that cannot be read past.
          `z-10` and not a reordering: the overlays are absolute with no z-index of their
          own, so none of them opens a stacking context and every label in the picture sits
          above every border in it, whatever order they were rendered in. */}
      {label && frame && (
        <span
          style={{
            left: `${x0 * 100}%`,
            top: `${y0 * 100}%`,
            transform: !labelInside && y0 > 0.06 ? 'translateY(-100%)' : undefined,
            ...(paint ? { backgroundColor: paint } : {}),
          }}
          className={`absolute z-10 max-w-[92%] truncate rounded-sm px-1 py-px text-[10px] font-semibold leading-tight text-white ${
            paint ? '' : off ? 'bg-slate-500/85 text-slate-100' : 'bg-blue-500/90'
          }`}
        >
          {label}
        </span>
      )}

      {/* HOW BIG THE PART IS, ON THE EDGES THAT SHOW IT. Two numbers at most: a square-on
          photograph shows two of a part's three dimensions, and the third points at the
          camera — see viewAxes, which is where the picture's direction picks the pair.
          Along the bottom edge and up the right one, inside the rectangle.
          `z-20`, a step above the names: a name says which box this is and can be found
          again by following the colour, where a measurement is a number that is either
          readable or gone. */}
      {across && frame && (
        <span
          style={{
            left: `${((x0 + x1) / 2) * 100}%`,
            top: `${y1 * 100}%`,
            // INSIDE the bottom edge. Outside, it sat on whatever the next part's box was
            // doing underneath — and the number belongs to the rectangle it is measuring,
            // which is the one thing that reads at a glance when six boxes overlap.
            transform: 'translate(-50%, -100%)',
            ...(paint ? { color: paint } : {}),
          }}
          className={`absolute z-20 whitespace-nowrap rounded-sm bg-slate-900/70 px-1 text-[10px] font-semibold leading-tight tabular-nums ${
            paint ? '' : 'text-blue-300'
          }`}
        >
          {across}
        </span>
      )}
      {up && frame && (
        <span
          style={{
            left: `${x1 * 100}%`,
            top: `${((y0 + y1) / 2) * 100}%`,
            // Read up the edge, so a tall box's height is not a column of single characters,
            // and inside it for the reason above.
            transform: 'translate(-100%, -50%) rotate(180deg)',
            writingMode: 'vertical-rl',
            ...(paint ? { color: paint } : {}),
          }}
          className={`absolute z-20 whitespace-nowrap rounded-sm bg-slate-900/70 px-px py-1 text-[10px] font-semibold leading-tight tabular-nums ${
            paint ? '' : 'text-blue-300'
          }`}
        >
          {up}
        </span>
      )}
    </span>
  );
}
